// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (API for pages/admin/*)
// Thin typed wrappers over the staff endpoints the backend already has (all require a staff role; writes need admin/editor):
// orders (GET /admin/orders, PUT /admin/orders/{ref}/status), complaints (GET /admin/claims, PUT /admin/claims/{ref}) and erasure requests
// (GET /admin/data-requests, PUT /admin/data-requests/{uid}), customers (GET /admin/customers). The backend enforces every rule; the UI hides what a role can't do.
// ─────────────────────────────────────────────────────────────────────────────
import type { Address } from "./addresses";
import type { Claim } from "./claims";
import { apiAuthed } from "./api";
import type { DropView, Landing } from "./drop";
import type { Order, OrderStatus } from "./orders";
import type { Plan } from "./plans";
import type { Wallet } from "./wallet";

export const ACTIVE: OrderStatus[] = ["placed", "confirmed", "preparing", "out_for_delivery"];

export const fetchAdminOrders = (statuses: OrderStatus[]) => apiAuthed<Order[]>("GET", `/admin/orders?${statuses.map((s) => `status=${s}`).join("&")}`);
export const setOrderStatus = (ref: string, status: OrderStatus, reasonCode?: string, note?: string) => apiAuthed<Order>("PUT", `/admin/orders/${encodeURIComponent(ref)}/status`, { status, ...(reasonCode ? { reasonCode } : {}), ...(note ? { note } : {}) });

/** The next step forward in the flow, if any. */
export const NEXT: Partial<Record<OrderStatus, OrderStatus>> = { placed: "confirmed", confirmed: "preparing", preparing: "out_for_delivery", out_for_delivery: "delivered" };

/** Today's Daily Catch including a draft (staff). */
export const fetchAdminDrop = () => apiAuthed<DropView>("GET", "/admin/drop");
export interface DropInput { label: string; dispatchAt: string | null; note: string | null; published: boolean }
export const saveDrop = (input: DropInput) => apiAuthed<DropView>("PUT", "/admin/drop", input);
export interface LandingInput { itemId: string; landedAt: string; boat: string | null; spec: string | null; grade: string | null; packsTotal: number; packsLeft: number; vesselReg: string | null; grossKg: number | null; netKg: number | null; purchasePriceInr: number | null }
export const saveLanding = (id: string, input: LandingInput) => apiAuthed<Landing>("PUT", `/admin/drop/landings/${encodeURIComponent(id)}`, input);
export const deleteLanding = (id: string) => apiAuthed<null>("DELETE", `/admin/drop/landings/${encodeURIComponent(id)}`);

export const fetchClaimQueue = () => apiAuthed<Claim[]>("GET", "/admin/claims");
/** Claims in one status (submitted = waiting, approved, rejected), oldest first (GET /admin/claims?status=). */
export const fetchClaimsByStatus = (status: Claim["status"]) => apiAuthed<Claim[]>("GET", `/admin/claims?status=${status}&limit=100`);
export const decideClaim = (ref: string, decision: "approved" | "rejected", creditInr: number, note: string) =>
  apiAuthed<Claim>("PUT", `/admin/claims/${encodeURIComponent(ref)}`, { decision, creditInr, ...(note.trim() ? { note: note.trim() } : {}) });

export interface ErasureItem {
  uid: string;
  status: "open" | "completed" | "declined";
  createdAt: string;
  note: string | null;
}
export const fetchErasureQueue = () => apiAuthed<ErasureItem[]>("GET", "/admin/data-requests");
export const decideErasure = (uid: string, decision: "completed" | "declined", note: string) =>
  apiAuthed<ErasureItem>("PUT", `/admin/data-requests/${encodeURIComponent(uid)}`, { decision, ...(note.trim() ? { note: note.trim() } : {}) });

/** One order with the customer's history, any complaint and the private staff note (GET /admin/orders/{ref}). */
export interface OrderDetail {
  order: Order;
  customerUid: string;
  customerOrderCount: number;
  customerComplaintCount: number;
  complaint: Claim | null;
  opsNote: string | null;
  opsNoteBy: string | null;
  opsNoteAt: string | null;
  /** The captain the order was handed to (staff only). */
  driverId: string | null;
  driverName: string | null;
  driverAssignedAt: string | null;
}
export const fetchOrderDetail = (ref: string) => apiAuthed<OrderDetail>("GET", `/admin/orders/${encodeURIComponent(ref)}`);
export const saveOpsNote = (ref: string, note: string) => apiAuthed<OrderDetail>("PUT", `/admin/orders/${encodeURIComponent(ref)}/note`, { note });

/** One account in the staff customer directory (GET /admin/customers): the profile plus what their orders, wallet and reviews add up to. */
export interface CustomerRow {
  uid: string;
  fullName: string | null;
  nickname: string | null;
  phone: string | null;
  email: string | null;
  joinedAt: string;
  profileCompleted: boolean;
  /** Orders that were not cancelled, and their rupee total. */
  orderCount: number;
  spent: number;
  lastOrderRef: string | null;
  lastOrderAt: string | null;
  lastAddress: string | null;
  lastCity: string | null;
  lastPincode: string | null;
  walletBalance: number;
  complaintCount: number;
  reviewCount: number;
  reviewAverage: number | null;
}
export interface CustomerDirectory {
  /** Every account, not just the newest 100 listed. */
  total: number;
  customers: CustomerRow[];
}
export const fetchCustomers = () => apiAuthed<CustomerDirectory>("GET", "/admin/customers");

/** One customer with everything behind the directory row (GET /admin/customers/{uid}); the note is private to staff. */
export interface CustomerDetail {
  customer: CustomerRow;
  referralCode: string;
  /** Newest first, every status. */
  orders: Order[];
  addresses: Address[];
  plans: Plan[];
  /** Balance plus the newest ten entries. */
  wallet: Wallet;
  claims: Claim[];
  opsNote: string | null;
  opsNoteBy: string | null;
  opsNoteAt: string | null;
}
export const fetchCustomerDetail = (uid: string) => apiAuthed<CustomerDetail>("GET", `/admin/customers/${encodeURIComponent(uid)}`);
export const saveCustomerNote = (uid: string, note: string) => apiAuthed<CustomerDetail>("PUT", `/admin/customers/${encodeURIComponent(uid)}/note`, { note });
/** Goodwill wallet credit (editors/admins; the backend caps one grant at ₹5,000). */
export const grantWalletCredit = (uid: string, amount: number, note: string) => apiAuthed<Wallet>("POST", `/admin/wallet/${encodeURIComponent(uid)}/credit`, { amount, note });

// ── Support tickets (grievance log; nothing is sent to customers from here) ──
export type TicketPriority = "p1" | "p2" | "p3";
export type TicketChannel = "whatsapp" | "app" | "email" | "phone" | "other";
export type TicketCategory = "delivery" | "quality" | "item" | "payment" | "fleet" | "other";
export type TicketMessageKind = "customer" | "reply" | "note";
export interface Ticket {
  id: string; subject: string; description: string | null; priority: TicketPriority; channel: TicketChannel; category: TicketCategory;
  status: "open" | "resolved"; customerName: string | null; customerPhone: string | null; orderRef: string | null; assignee: string | null;
  createdBy: string; createdAt: string; updatedAt: string; firstResponseAt: string | null; resolvedAt: string | null; resolvedBy: string | null;
  dueAt: string; withinSla: boolean | null; messages: { kind: TicketMessageKind; body: string; by: string; at: string }[];
}
export interface NewTicket { subject: string; description: string; priority: TicketPriority; channel: TicketChannel; category: TicketCategory; customerName: string; customerPhone?: string; orderRef: string; assignee: string }
export const fetchTickets = () => apiAuthed<{ tickets: Ticket[] }>("GET", "/admin/tickets").then((r) => r.tickets);
export const createTicket = (body: NewTicket) => apiAuthed<Ticket>("POST", "/admin/tickets", body);
export const updateTicket = (id: string, body: Partial<Pick<Ticket, "status" | "priority" | "category" | "assignee">>) => apiAuthed<Ticket>("PUT", `/admin/tickets/${id}`, body);
export const addTicketMessage = (id: string, kind: TicketMessageKind, body: string, resolve = false) => apiAuthed<Ticket>("POST", `/admin/tickets/${id}/messages`, { kind, body, resolve });
